import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getVesselById } from '../../firebase/services/vesselService';
import { getReservationsByVessel } from '../../firebase/services/reservationService';
import { Vessel, Reservation } from '../../models/types';
import { format, parseISO } from 'date-fns';
import AvailabilityCalendar from '../../components/AvailabilityCalendar';
import MarinaAdminLayout from '../../layouts/MarinaAdminLayout';
import { formatTime12Hour } from '../../utils/formatters';

const VesselAvailabilityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVesselAndReservations = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const [vesselData, reservationsData] = await Promise.all([
          getVesselById(id),
          getReservationsByVessel(id)
        ]);

        if (!vesselData) {
          setError('Vessel not found');
          return;
        }

        setVessel(vesselData);
        setReservations(reservationsData);
      } catch (err) {
        console.error('Error fetching vessel data:', err);
        setError('Failed to load vessel data');
      } finally {
        setLoading(false);
      }
    };

    fetchVesselAndReservations();
  }, [id]);

  const handleDateSelect = (date: Date) => {
    // This is just a placeholder for now
  };

  const handleTimeSlotSelect = (timeSlot: string) => {
    // This is just a placeholder for now
  };

  if (loading) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </MarinaAdminLayout>
    );
  }

  if (error || !vessel) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            {error || 'Failed to load vessel data'}
          </Alert>
        </Box>
      </MarinaAdminLayout>
    );
  }

  return (
    <MarinaAdminLayout>
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/dashboard')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" component="h1" gutterBottom>
              {vessel.name} - Availability
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Vessel Details
                </Typography>
                <Typography><strong>Type:</strong> {vessel.type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</Typography>
                <Typography><strong>Capacity:</strong> {vessel.capacity} people</Typography>
                <Typography><strong>Status:</strong> {vessel.isActive ? 'Active' : 'Inactive'}</Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                  Current Reservations
                </Typography>
                {reservations.length > 0 ? (
                  <Box>
                    {reservations.map((reservation) => (
                      <Box key={reservation.id} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {format(parseISO(reservation.date), 'MMMM d, yyyy')} - {formatTime12Hour(reservation.timeSlot)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No current reservations
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Availability Calendar
              </Typography>
              <AvailabilityCalendar
                vesselType={vessel.type}
                onDateSelect={handleDateSelect}
                onTimeSlotSelect={handleTimeSlotSelect}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </MarinaAdminLayout>
  );
};

export default VesselAvailabilityPage; 